// 微信回调字段统一维护，业务参数（帖子链接、板块、折扣码等）不能随授权参数一起删除。
export const WX_AUTH_KEYS = [
  'openId',
  'sessionKey',
  'unionId',
  'nickName',
  'subscribe',
  'isReceive',
  'isReceiveFt',
  'isReceiveActivity',
];

// 修复历史回跳地址中的 #/plate#/plate，以及被重复路由截断的授权参数。
// 只拆分原始井号，不解码整个地址，避免破坏 href 等业务参数内部的转义字符。
export function normalizeWxCallbackUrl(href) {
  const url = new URL(href);
  const fragments = url.hash.slice(1).split('#');
  if (!fragments.slice(1).some(fragment => fragment.startsWith('/'))) return url.href;
  const first = fragments[0];
  const queryIndex = first.indexOf('?');
  const routePath = queryIndex === -1 ? first : first.slice(0, queryIndex);
  const query = new URLSearchParams(queryIndex === -1 ? '' : first.slice(queryIndex + 1));
  fragments.slice(1).forEach(fragment => {
    const start = fragment.search(/[?&]/);
    if (start === -1) return;
    new URLSearchParams(fragment.slice(start + 1)).forEach((value, key) => {
      if (WX_AUTH_KEYS.includes(key)) query.set(key, value);
    });
  });
  const search = query.toString();
  url.hash = `${routePath || '/plate'}${search ? '?' + search : ''}`;
  return url.href;
}

// 每次授权都从干净地址重新构造 backUrl，禁止携带上一轮授权结果和缓存时间戳。
export function buildWxBackUrl(href) {
  const url = new URL(normalizeWxCallbackUrl(href));
  const cleanKeys = [...WX_AUTH_KEYS, '_v'];
  cleanKeys.forEach(key => url.searchParams.delete(key));
  const hash = url.hash.slice(1);
  const queryIndex = hash.indexOf('?');
  const routePath = queryIndex === -1 ? hash : hash.slice(0, queryIndex);
  const query = new URLSearchParams(queryIndex === -1 ? '' : hash.slice(queryIndex + 1));
  cleanKeys.forEach(key => query.delete(key));
  const search = query.toString();
  url.hash = `${routePath || '/plate'}${search ? '?' + search : ''}`;
  return url.href;
}

const AUTH_ATTEMPT_KEY = 'wx_auth_attempt_time';
const AUTH_CACHE_RESET_KEY = 'wx_auth_cache_reset_time';
const AUTH_RETRY_INTERVAL = 60000;
export const WX_NOT_SUBSCRIBED_MESSAGE = '尚未关注公众号，请关注「一尘盯盘助手」后稍后重新进入';
let authorizationPromise = null;

// 回调本身也记为一次授权，兼容直接打开旧回调链接或从其他入口进入的用户。
export function recordWxAuthCallback(params) {
  if (params.openId || params.sessionKey || typeof params.subscribe !== 'undefined') {
    window.sessionStorage.setItem(AUTH_ATTEMPT_KEY, String(Date.now()));
  }
}

// 路由守卫、接口拦截器和登录页共用同一请求；跨页面跳转的冷却记录用于切断授权循环。
export function startWxAuthorization(login, href = window.location.href) {
  if (authorizationPromise) return authorizationPromise;
  const lastAttempt = Number(window.sessionStorage.getItem(AUTH_ATTEMPT_KEY) || 0);
  if (Date.now() - lastAttempt < AUTH_RETRY_INTERVAL) {
    if (window.localStorage.getItem('subscribe') === 'false') {
      return Promise.reject(new Error(WX_NOT_SUBSCRIBED_MESSAGE));
    }
    const lastCacheReset = Number(window.sessionStorage.getItem(AUTH_CACHE_RESET_KEY) || 0);
    if (Date.now() - lastCacheReset < AUTH_RETRY_INTERVAL) {
      return Promise.reject(new Error('微信授权后仍未取得有效登录状态，请稍后重新进入'));
    }
    // 授权回调后仍无有效登录态时清空全部 Web Storage，再用干净状态重试一次。
    window.localStorage.clear();
    window.sessionStorage.clear();
    window.openId = null;
    window.extra_user_token = null;
    // 清空后仅记录本轮重试时间，避免服务端持续失败时形成无限授权跳转。
    window.sessionStorage.setItem(AUTH_CACHE_RESET_KEY, String(Date.now()));
    return startWxAuthorization(login, href);
  }
  window.sessionStorage.setItem(AUTH_ATTEMPT_KEY, String(Date.now()));
  authorizationPromise = Promise.resolve()
    .then(() => login({ backUrl: encodeURIComponent(buildWxBackUrl(href)) }))
    .then(res => {
      if (!res || typeof res.data !== 'string' || !res.data) {
        throw new Error('未获取到微信授权地址，请稍后重试');
      }
      window.location.replace(res.data);
    })
    .catch(error => {
      // 请求失败后允许重试；成功发起跳转则保持锁，避免离开页面前再次授权。
      authorizationPromise = null;
      window.sessionStorage.removeItem(AUTH_ATTEMPT_KEY);
      throw error;
    });
  return authorizationPromise;
}
